import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Field } from '@navozyme/uikit/dist/atoms/Field';
import { DatePickerTimeInput } from '@navozyme/uikit/dist/molecules/DatePicker';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { AppDispatch } from 'app/store';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';

import {
  createPortcallStatusLog,
  DangerousGoodsData,
  DangerousGoodsStatus,
  getDangerousGoods,
  getDangerousGoodsData,
  getPortcallStatusLog,
  selectDangerousGoods,
  updateDangerousGoodsData,
} from '../../../slices/portCallSlice';

import DangerousGoodsTable from './DangerousGoodsTable';
import Input from '../../../../forms/components/Input';
import RadioButtonsCustom from '../../../../forms/components/RadioButtonsCustom';
import { booleanOptionsForRadioGroup } from '../../../../forms/helpers/random-form-helpers';
import { FormRefContext } from '../../../../global/context/FormRefContext';
import { removeNullUndefined } from '../../../../global/utils/cleanEmptyData';
import Page from '../../../../layout/components/Page';
import PageHeader from '../../../../layout/components/PageHeader';
import { isEmpty } from '../../../validations/FieldsValidation';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';

import '../styles/dangerous-goods.scss';

const validationSchema = Yup.object({
  dangerousGoodsType: Yup.array(),
  isTransportingExplosives: Yup.boolean().required('This field is required'),
  internationalSafetyManagementCertificate: Yup.object({
    shipHasInternationalSafetyManagement: Yup.boolean().required(
      'This field is required'
    ),
    certificateNumber: Yup.string().when(
      'shipHasInternationalSafetyManagement',
      (shipHasInternationalSafetyManagement, schema) => {
        if (
          shipHasInternationalSafetyManagement &&
          shipHasInternationalSafetyManagement[0]
        ) {
          return schema.required('This field is required');
        } else {
          return schema.notRequired();
        }
      }
    ),
    issuedBy: Yup.string().when(
      'shipHasInternationalSafetyManagement',
      (shipHasInternationalSafetyManagement, schema) => {
        if (
          shipHasInternationalSafetyManagement &&
          shipHasInternationalSafetyManagement[0]
        ) {
          return schema.required('This field is required');
        } else {
          return schema.notRequired();
        }
      }
    ),
    expireDate: Yup.string().when(
      'shipHasInternationalSafetyManagement',
      (shipHasInternationalSafetyManagement, schema) => {
        if (
          shipHasInternationalSafetyManagement &&
          shipHasInternationalSafetyManagement[0]
        ) {
          return schema.required('This field is required');
        } else {
          return schema.notRequired();
        }
      }
    ),
    reasonWhyNot: Yup.string().when(
      'shipHasInternationalSafetyManagement',
      (shipHasInternationalSafetyManagement, schema) => {
        if (
          shipHasInternationalSafetyManagement &&
          shipHasInternationalSafetyManagement[0] === false
        ) {
          return schema.required('This field is required');
        } else {
          return schema.notRequired();
        }
      }
    ),
  }).nonNullable(),
});

const IS_NOT_CARRYING_DANGEROUS_GOODS = 'isNotCarryingDangerousGoods';
const IS_UNLOADING_DANGEROUS_GOODS = 'isUnloadingDangerousGoods';
const IS_LOADING_DANGEROUS_GOODS = 'isLoadingDangerousGoods';
const IS_DANGEROUS_GOODS_IN_TRANSIT = 'isDangerousGoodsInTransit';

const allDangerousGoodsStatusValues = [
  IS_NOT_CARRYING_DANGEROUS_GOODS,
  IS_UNLOADING_DANGEROUS_GOODS,
  IS_LOADING_DANGEROUS_GOODS,
  IS_DANGEROUS_GOODS_IN_TRANSIT,
];

const DangerousGoods = () => {
  const formRef: any = useRef(null);
  const { formContextRef } = useContext(FormRefContext);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams();
  const dangerousGoods = useSelector(selectDangerousGoods);

  const [initialValues, setInitialValues] = useState<
    Partial<DangerousGoodsData>
  >({
    dangerousGoodsType: [IS_NOT_CARRYING_DANGEROUS_GOODS],
  });

  useEffect(() => {
    formContextRef.current = formRef.current;
  }, [formContextRef, formRef, dangerousGoods]);

  useEffect(() => {
    let newInitialValues = { ...initialValues };
    const { dangerousGoodsData } = dangerousGoods;
    if (dangerousGoodsData) {
      newInitialValues = {
        ...newInitialValues,
        ...JSON.parse(JSON.stringify(dangerousGoodsData)),
        isTransportingExplosives: dangerousGoodsData.isTransportingExplosives,
      };
    }

    if (dangerousGoodsData && dangerousGoodsData?.dangerousGoodsStatus) {
      newInitialValues = { ...newInitialValues, dangerousGoodsType: [] };
      if (
        dangerousGoodsData?.dangerousGoodsStatus[IS_DANGEROUS_GOODS_IN_TRANSIT]
      ) {
        newInitialValues.dangerousGoodsType?.push(
          IS_DANGEROUS_GOODS_IN_TRANSIT
        );
      }
      if (
        dangerousGoodsData?.dangerousGoodsStatus[IS_UNLOADING_DANGEROUS_GOODS]
      ) {
        newInitialValues.dangerousGoodsType?.push(IS_UNLOADING_DANGEROUS_GOODS);
      }
      if (
        dangerousGoodsData?.dangerousGoodsStatus[IS_LOADING_DANGEROUS_GOODS]
      ) {
        newInitialValues.dangerousGoodsType?.push(IS_LOADING_DANGEROUS_GOODS);
      }
      if (
        dangerousGoodsData?.dangerousGoodsStatus[
          IS_NOT_CARRYING_DANGEROUS_GOODS
        ]
      ) {
        newInitialValues = {
          ...newInitialValues,
          dangerousGoodsType: [IS_NOT_CARRYING_DANGEROUS_GOODS],
        };
      }
    }

    if (
      dangerousGoodsData &&
      dangerousGoodsData.internationalSafetyManagementCertificate &&
      dangerousGoodsData.internationalSafetyManagementCertificate.expireDate
    ) {
      newInitialValues.internationalSafetyManagementCertificate = {
        ...newInitialValues.internationalSafetyManagementCertificate,
        expireDate: new Date(
          dangerousGoodsData.internationalSafetyManagementCertificate.expireDate
        ),
      };
    }

    newInitialValues.internationalSafetyManagementCertificate = {
      ...newInitialValues.internationalSafetyManagementCertificate,
      shipHasInternationalSafetyManagement:
        dangerousGoodsData?.internationalSafetyManagementCertificate
          ?.shipHasInternationalSafetyManagement,
      certificateNumber:
        dangerousGoodsData?.internationalSafetyManagementCertificate
          ?.certificateNumber,
      issuedBy:
        dangerousGoodsData?.internationalSafetyManagementCertificate?.issuedBy,
      expireDate:
        newInitialValues?.internationalSafetyManagementCertificate?.expireDate,
      reasonWhyNot:
        dangerousGoodsData?.internationalSafetyManagementCertificate
          ?.reasonWhyNot,
    };
    setInitialValues(newInitialValues);
  }, [dangerousGoods, dangerousGoods.dangerousGoodsData]);

  useEffect(() => {
    if (params.uid) {
      dispatch(getDangerousGoodsData(params.uid));
      dispatch(getDangerousGoods(params.uid));
    }
  }, [dispatch, params.uid]);

  const handleSubmit = async (values: Partial<DangerousGoodsData>) => {
    const finalValues: DangerousGoodsData = {
      ...values,
      dangerousGoodsStatus: null,
      dangerousGoodsType: [],
    };

    if (values.internationalSafetyManagementCertificate?.expireDate) {
      finalValues.internationalSafetyManagementCertificate = {
        ...finalValues.internationalSafetyManagementCertificate,
        expireDate: (
          finalValues.internationalSafetyManagementCertificate
            ?.expireDate as Date
        ).toISOString(),
      };
    }

    const dangerousGoodsStatus: DangerousGoodsStatus = {
      isDangerousGoodsInTransit: false,
      isLoadingDangerousGoods: false,
      isNotCarryingDangerousGoods: false,
      isUnloadingDangerousGoods: false,
    };

    values.dangerousGoodsType?.forEach((key: string) => {
      dangerousGoodsStatus[key as keyof DangerousGoodsStatus] = true;
    });

    allDangerousGoodsStatusValues.forEach((key) => {
      if (!dangerousGoodsStatus[key as keyof DangerousGoodsStatus]) {
        dangerousGoodsStatus[key as keyof DangerousGoodsStatus] = false;
      }
    });

    finalValues.dangerousGoodsStatus = dangerousGoodsStatus;
    finalValues.dangerousGoodsType = undefined;

    if (params.uid) {
      const isSuccess = await dispatch(
        updateDangerousGoodsData({
          uid: params.uid,
          dangerousGoodsData: removeNullUndefined(
            finalValues
          ) as DangerousGoodsData,
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccess) return;
    }

    if (params.uid) {
      const isSuccessUpdateArrivalDGStatus = await dispatch(
        createPortcallStatusLog({
          statusLog: {
            uid: params.uid,
            section: DossierSectionOptions.ArrivalDangerousGoodsDeclaration,
            status: DossierSectionStatusOptions.SubmittedToNmap,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccessUpdateArrivalDGStatus) return;

      const isSuccessUpdateDepartureDPStatus = await dispatch(
        createPortcallStatusLog({
          statusLog: {
            uid: params.uid,
            section: DossierSectionOptions.DepartureDangerousGoodsDeclaration,
            status: DossierSectionStatusOptions.SubmittedToNmap,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccessUpdateDepartureDPStatus) return;

      dispatch(getPortcallStatusLog(params.uid));
    }
    navigate(`/${params.uid}/general-declaration`);
  };
  return (
    <Page>
      <div className="dangerous-goods">
        <PageHeader title={'Dangerous Goods'} />
        <Formik
          innerRef={formRef}
          enableReinitialize
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}>
          {({ values, setFieldValue }) => {
            return (
              <>
                <Form className="dangerous-goods-form">
                  <div className="dangerous-goods__questions">
                    <RadioButtonsCustom
                      name="isTransportingExplosives"
                      label="Does the ship transport explosives? *"
                      options={booleanOptionsForRadioGroup()}
                    />
                    <>
                      <RadioButtonsCustom
                        name="internationalSafetyManagementCertificate.shipHasInternationalSafetyManagement"
                        label="Does the ship has a valid International Safety Management certificate? *"
                        options={booleanOptionsForRadioGroup()}
                      />
                      <div className="ism-extra-fields-section">
                        {values?.internationalSafetyManagementCertificate &&
                        values?.internationalSafetyManagementCertificate
                          ?.shipHasInternationalSafetyManagement ? (
                          <>
                            <div className="dangerous-goods-form__certificate-number">
                              <Input
                                name="internationalSafetyManagementCertificate.certificateNumber"
                                placeholder="Certificate  number"
                                label="Certificate number *"
                              />
                            </div>
                            <div className="dangerous-goods-form__row">
                              <Input
                                name="internationalSafetyManagementCertificate.issuedBy"
                                placeholder="Issued by (name of entity)"
                                label="Issued by (name of entity) *"
                              />
                              <Field
                                label="Expiry date *"
                                className="field_date_picker">
                                <DatePickerTimeInput
                                  key={
                                    initialValues
                                      ?.internationalSafetyManagementCertificate
                                      ?.expireDate + 'expireDate'
                                  }
                                  name="internationalSafetyManagementCertificate.expireDate"
                                  sizeInput="large"
                                  minDate={new Date()}
                                  showTime={false}
                                  defaultValue={
                                    initialValues
                                      ?.internationalSafetyManagementCertificate
                                      ?.expireDate
                                  }
                                  action={({ isoDate }: { isoDate: string }) =>
                                    setFieldValue(
                                      'internationalSafetyManagementCertificate.expireDate',
                                      isoDate ? new Date(isoDate) : undefined
                                    )
                                  }
                                  hasError={
                                    isEmpty(
                                      values
                                        ?.internationalSafetyManagementCertificate
                                        ?.expireDate
                                    ) &&
                                    formRef.current?.touched
                                      ?.internationalSafetyManagementCertificate
                                      ?.expireDate
                                  }
                                  errorMessage={'Expiry date is required'}
                                />
                              </Field>
                            </div>
                          </>
                        ) : null}
                        {values?.internationalSafetyManagementCertificate &&
                        values?.internationalSafetyManagementCertificate
                          ?.shipHasInternationalSafetyManagement == false ? (
                          <Input
                            name="internationalSafetyManagementCertificate.reasonWhyNot"
                            placeholder="Why not?"
                            label="Why not? *"
                          />
                        ) : null}
                      </div>
                    </>
                  </div>
                  <div className="dangerous-goods-form__contact-information">
                    <h3>Provide the Dangerous Goods contact information:</h3>
                    <div>
                      <Input
                        name="dangerousGoodsContactPerson.name"
                        placeholder="Full name"
                        label="Full name"
                      />
                      <Input
                        name="dangerousGoodsContactPerson.details"
                        placeholder="Contact details"
                        label="Contact details"
                      />
                    </div>
                  </div>
                  {values?.dangerousGoodsType &&
                  values?.dangerousGoodsType.length > 0 &&
                  values?.dangerousGoodsType[0] !==
                    IS_NOT_CARRYING_DANGEROUS_GOODS ? (
                    <>
                      <h3>
                        Please, provide the information about the dangerous
                        goods you are carrying
                      </h3>
                      <DangerousGoodsTable />
                    </>
                  ) : null}
                  <SkipAndNextButtons
                    next={`/${params.uid}/general-declaration`}
                    back={`/${params.uid}/cargo`}
                  />
                </Form>
              </>
            );
          }}
        </Formik>
      </div>
    </Page>
  );
};

export default DangerousGoods;
